import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Profiles, type UserProfile } from '@/lib/api';
import { useChatStore } from '@/stores/chat';
import UserAvatar from './UserAvatar';
import { formatDateDivider, fromNow } from '@/lib/format';

interface ProfilePanelProps {
  username: string | null; // null = panel closed
  onClose: () => void;
}

/**
 * Right-side slide-in panel showing another user's public profile. Loading
 * state shows a skeleton; fetch failure shows a retry button.
 */
export default function ProfilePanel({ username, onClose }: ProfilePanelProps) {
  const titleId = 'cf-profile-panel-title';
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const presenceByUser = useChatStore((s) => s.presenceByUser);

  useEffect(() => {
    if (!username) {
      setProfile(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Profiles.get(username)
      .then((r) => {
        if (cancelled) return;
        setProfile(r.user);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message: unknown }).message)
            : 'Could not load profile';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  const open = username !== null;
  const livePresence = profile ? presenceByUser.get(profile.id) : undefined;
  const lastSeen = livePresence?.lastSeen ?? profile?.lastSeen ?? null;

  return (
    <div
      role="presentation"
      aria-hidden={!open}
      className={clsx(
        'fixed inset-0 z-40 flex justify-end bg-black/30 transition-opacity',
        open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={clsx(
          'flex h-full w-full max-w-sm flex-col border-l border-ink-100 bg-white shadow-soft',
          'dark:border-ink-700 dark:bg-ink-800',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-ink-700">
          <h2 id={titleId} className="text-lg font-semibold">
            User profile
          </h2>
          <button type="button" aria-label="Close" className="btn-ghost px-2" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="nice-scroll flex-1 overflow-y-auto p-6">
          {loading && <ProfileSkeleton />}
          {error && !loading && (
            <div className="space-y-3 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  // Re-trigger the effect by toggling through parent, but
                  // we can also just re-call directly here.
                  if (!username) return;
                  setLoading(true);
                  setError(null);
                  Profiles.get(username)
                    .then((r) => setProfile(r.user))
                    .catch((e: unknown) => {
                      const msg =
                        e && typeof e === 'object' && 'message' in e
                          ? String((e as { message: unknown }).message)
                          : 'Could not load profile';
                      setError(msg);
                    })
                    .finally(() => setLoading(false));
                }}
              >
                Retry
              </button>
            </div>
          )}
          {profile && !loading && (
            <div className="flex flex-col items-center text-center">
              <UserAvatar
                name={profile.displayName || profile.username}
                src={profile.avatarUrl}
                size={96}
                className="mb-4"
              />
              <h3 className="text-xl font-semibold">{profile.displayName || profile.username}</h3>
              <p className="text-sm text-brand-600 dark:text-brand-300">@{profile.username}</p>

              <dl className="mt-6 w-full space-y-3 text-left text-sm">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-300">
                    Member since
                  </dt>
                  <dd className="mt-1">{formatDateDivider(profile.createdAt)}</dd>
                </div>
                {lastSeen && (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-300">
                      Last seen
                    </dt>
                    <dd className="mt-1">{fromNow(lastSeen)}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex animate-pulse flex-col items-center">
      <div className="mb-4 h-24 w-24 rounded-full bg-ink-200 dark:bg-ink-700" />
      <div className="mb-2 h-5 w-32 rounded bg-ink-200 dark:bg-ink-700" />
      <div className="h-4 w-24 rounded bg-ink-200 dark:bg-ink-700" />
      <div className="mt-6 w-full space-y-3">
        <div className="h-3 w-24 rounded bg-ink-200 dark:bg-ink-700" />
        <div className="h-4 w-40 rounded bg-ink-200 dark:bg-ink-700" />
      </div>
    </div>
  );
}